import React, { useEffect, useRef, useState } from "react";
import { Images } from "../../utils/Images";
import { Box, Typography, useMediaQuery, Button } from "@mui/material";
import { useTheme, styled } from "@mui/material/styles";
import { CampaignButtonNoArrowVariant } from "../../components/common/Button/Button";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { makeDonationAction } from "../../store/slices/donationSlice";
import { getCampaignById } from "../../api/services/campaignService";
import TempleThankYou from "./TempleThankYou";
import { isTempleCampaign } from "../../utils/templeCampaign";

const ThankYouBanner = () => {
  const theme = useTheme();
  const isLaptop = useMediaQuery(theme.breakpoints.down("lg"));
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery("(min-width: 600px) and (max-width: 900px)");
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);

  // Local dev only: /thankyou-Page?preview=temple&campaignId=<id> shows the page with sample
  // payment data and records nothing. import.meta.env.DEV is false in production builds, so
  // this never ships.
  const previewState = (() => {
    if (!import.meta.env.DEV || location.state) return null;
    const params = new URLSearchParams(location.search);
    if (params.get("preview") !== "temple") return null;
    return {
      preview: true,
      status: "Success",
      data: { razorpay_payment_id: "pay_PREVIEW0001", amount: 2268, currency: "INR", paymentMethod: "upi", tipAmount: 168 },
      details: { campaignId: params.get("campaignId") || "", fullName: "Abhijeet Jharbade", amount: 2268, currency: "INR", tipAmount: 168 },
    };
  })();
  const isPreview = Boolean(previewState);

  // Get payment data from navigation state
  const paymentState = location.state || previewState || {};
  const paymentData = paymentState.data || {};
  const paymentDetails = paymentState.details || {};
  const status = paymentState.status || "Success";
  const isSuccess = status === "Success";

  const donationProcessedRef = useRef(false);
  const purchaseTrackedRef = useRef(false);

  // Redirect to failure page if payment failed
  useEffect(() => {
    if (!isSuccess) {
      navigate('/payment-failure', { state: paymentState, replace: true });
    }
  }, [isSuccess, paymentState, navigate]);

  // Process donation after successful payment
  useEffect(() => {
    // Early return if conditions aren't met or already processed
    if (isPreview || !paymentData || !paymentDetails || donationProcessedRef.current || !isSuccess) {
      return;
    }

    // Extract transaction ID from different payment methods
    const transactionId =
      paymentData.razorpay_payment_id ||
      paymentData.paymentId ||
      paymentData.capture?.purchase_units?.[0]?.payments?.captures?.[0]?.id ||
      paymentData.sessionId ||
      null;

    // Extract amount from different payment methods
    const donationAmount =
      paymentData.amount ||
      paymentDetails.amount ||
      paymentData.capture?.purchase_units?.[0]?.payments?.captures?.[0]?.amount?.value ||
      0;

    // Determine payment type from details
    const paymentType = paymentDetails.paymentType || 
                       (paymentDetails.campaignName ? 'campaign' : 'donation') ||
                       'donation';

    // Determine if overseas payment
    const isOverseas = paymentDetails.donationType === "overseas" || 
                      paymentDetails.currency !== "INR" ||
                      paymentData.currency !== "INR";

    // Extract mobile number from all possible locations
    const mobileNumber = 
      paymentDetails.phone || 
      paymentDetails.phoneNumber || 
      paymentDetails.mobile || 
      paymentDetails.contact || 
      paymentDetails.contactNumber ||
      paymentData.userDetails?.phone ||
      paymentData.userDetails?.phoneNumber ||
      paymentData.userDetails?.mobile ||
      paymentData.userDetails?.contact ||
      paymentData.contact ||
      paymentData.phoneNumber ||
      paymentData.phone ||
      paymentData.mobile ||
      '';

    const donationDatas = {
      userId: paymentDetails.userId || user?.id || null,
      donation_cause: paymentType === 'campaign' ? paymentDetails.campaignName : null,
      transaction_id: transactionId,
      amount: donationAmount,
      paymentType: paymentType,
      animalId: paymentDetails.adoptanimalId || paymentDetails.animalId || null,
      over_seas: isOverseas ? 1 : 0,
      status: isSuccess ? 1 : 0,
      fullName: paymentDetails.fullName || paymentDetails.name || paymentData.name || '',
      email: paymentDetails.email || paymentData.email || '',
      mobileNumber: mobileNumber
    };

    const processDonation = async () => {
      try {
        // Mark as processing to prevent duplicate calls
        donationProcessedRef.current = true;
        // Make donation API call
        await dispatch(makeDonationAction(donationDatas)).unwrap();
      } catch (error) {
        console.error('Donation processing error:', error);
        // Don't block the UI if donation recording fails
        // Keep the ref as true to prevent retry
      }
    };

    processDonation();
  }, [paymentData, paymentDetails, dispatch, isSuccess, user]);

  // Google Tag Manager — purchase event.
  //
  // This is a client-side route, so the GTM snippet in index.html has already
  // run long before we get here. A <script> tag placed in this page would never
  // execute on navigation, so the push has to happen from JS on mount.
  //
  // Guarded twice on purpose:
  //   - the ref covers re-renders and StrictMode's double-invoked effects
  //   - sessionStorage, keyed on the order id, covers a page refresh. The
  //     browser preserves history state across a reload, so location.state
  //     survives and the effect would otherwise report the same purchase again.
  useEffect(() => {
    if (isPreview || !isSuccess || purchaseTrackedRef.current) return;

    const orderId =
      paymentData.razorpay_order_id ||
      paymentData.razorpay_subscription_id ||
      paymentData.orderId ||
      paymentData.sessionId ||
      paymentData.capture?.purchase_units?.[0]?.payments?.captures?.[0]?.id ||
      null;

    const orderValue = Number(
      paymentData.amount ??
        paymentDetails.amount ??
        paymentData.capture?.purchase_units?.[0]?.payments?.captures?.[0]?.amount
          ?.value ??
        0
    );

    // Razorpay sends "0" rather than omitting unused id fields.
    if (!orderId || orderId === "0") return;
    if (!Number.isFinite(orderValue) || orderValue <= 0) return;

    const dedupeKey = `gtm_purchase_${orderId}`;
    try {
      if (sessionStorage.getItem(dedupeKey)) return;
      sessionStorage.setItem(dedupeKey, "1");
    } catch {
      // Private mode / storage disabled — the ref guard still covers this session.
    }

    purchaseTrackedRef.current = true;

    // Extract user data for Enhanced Conversions (Google Ads)
    // Values are unhashed — GTM / the Google Ads Enhanced Conversions tag hashes before sending
    const ecEmail =
      paymentDetails.email || paymentData.email || user?.email || "";

    const rawPhone =
      paymentDetails.phone ||
      paymentDetails.phoneNumber ||
      paymentDetails.mobile ||
      paymentDetails.contact ||
      paymentData.phone ||
      paymentData.contact ||
      "";
    const phoneDigits = String(rawPhone).replace(/\D/g, "");
    const ecPhone =
      phoneDigits.length === 10
        ? `+91${phoneDigits}`
        : phoneDigits.length === 12 && phoneDigits.startsWith("91")
          ? `+${phoneDigits}`
          : phoneDigits.length === 11 && phoneDigits.startsWith("0")
            ? `+91${phoneDigits.slice(1)}`
            : rawPhone
              ? `+${phoneDigits}`
              : "";

    const fullName = (
      paymentDetails.fullName ||
      paymentDetails.name ||
      paymentData.name ||
      user?.name ||
      ""
    ).trim();
    const nameParts = fullName.split(/\s+/);
    const ecFirstName = nameParts[0] || "";
    const ecLastName = nameParts.slice(1).join(" ") || "";

    const ecTransactionId =
      paymentData.razorpay_payment_id ||
      paymentData.paymentId ||
      String(orderId);

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "gtm.purchase",
      order_id: String(orderId),
      order_value: orderValue,
      currency: paymentData.currency || paymentDetails.currency || "INR",
      transaction_id: ecTransactionId,
      // Enhanced Conversions user_data — read by Google Ads EC tag in GTM
      user_data: {
        email_address: ecEmail,
        phone_number: ecPhone,
        first_name: ecFirstName,
        last_name: ecLastName,
      },
    });
  }, [isSuccess, paymentData, paymentDetails, user]);

  // Format currency amount
  const formatAmount = (amount, currency = "INR") => {
    if (!amount) return "₹0";
    const currencySymbols = {
      INR: "₹",
      USD: "$",
      AED: "د.إ",
      CAD: "C$",
      AUD: "A$",
      GBP: "£",
    };
    const symbol = currencySymbols[currency] || currency;
    if (currency === "INR") {
      return `${symbol}${Number(amount).toLocaleString("en-IN")}`;
    }
    return `${symbol}${Number(amount).toLocaleString()}`;
  };

  // Extract transaction details
  const transactionId =
    paymentData.razorpay_payment_id ||
    paymentData.paymentId ||
    "N/A";
  const paymentDateTime =
    paymentData.paymentDateTime ||
    (paymentData.formattedDate && paymentData.formattedTime
      ? `${paymentData.formattedDate} | ${paymentData.formattedTime}`
      : new Date().toLocaleString("en-US", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        }));
  const paymentMethod =
    paymentData.paymentMethod || "UPI (Unified Payments Interface)";
  const amount = paymentData.amount || paymentDetails.amount || 0;
  const currency = paymentData.currency || paymentDetails.currency || "INR";

  // Temple campaigns get their own thank-you page. Look the campaign up once; until we know,
  // show nothing rather than flash the regular page.
  // campaignId: the campaign credited. sourceCampaignId: the temple page the donor paid from (the
  // temple box credits a sub-category, not the campaign).
  const thankYouCampaignId =
    paymentDetails.campaignId ||
    paymentDetails.sourceCampaignId ||
    paymentData.userDetails?.campaignId ||
    paymentData.userDetails?.sourceCampaignId ||
    null;
  // Paid from a temple donation box (temple homepage or a temple campaign page). Homepage gifts
  // carry no campaign at all, so this flag — not the campaign lookup — decides for them.
  const isTempleGift = paymentDetails.thankYouVariant === "temple";
  const [templeCampaign, setTempleCampaign] = useState(undefined); // undefined = checking, null = not a temple
  useEffect(() => {
    if (!isSuccess || !thankYouCampaignId) {
      setTempleCampaign(null);
      return undefined;
    }
    let cancelled = false;
    getCampaignById(thankYouCampaignId)
      .then((res) => !cancelled && setTempleCampaign(isTempleGift || isTempleCampaign(res?.data) ? res.data || null : null))
      .catch(() => !cancelled && setTempleCampaign(null));
    return () => {
      cancelled = true;
    };
  }, [isSuccess, thankYouCampaignId, isTempleGift]);

  // Handle 80G form navigation
  const handle80GForm = () => {
    navigate("/profile/80g-form", {
      state: {
        transactionId: transactionId,
        details: paymentDetails,
      },
    });
  };

  if (isSuccess && templeCampaign === undefined && !isTempleGift) {
    return <Box sx={{ minHeight: "70vh" }} />;
  }

  if (isSuccess && (templeCampaign || isTempleGift)) {
    const isMonthlyThankYou =
      location.pathname.toLowerCase().startsWith("/monthly/") ||
      String(paymentDetails.donationType || "").toLowerCase() === "monthly";
    const paidOn = paymentData.formattedDate ? new Date(paymentData.formattedDate) : new Date();
    const methodLabel = String(paymentData.paymentMethod || "").toLowerCase();
    return (
      <TempleThankYou
        campaign={templeCampaign || null}
        campaignId={thankYouCampaignId}
        fallbackTitle={paymentDetails.campaignName}
        amount={amount}
        tipAmount={paymentDetails.tipAmount || paymentData.tipAmount || 0}
        currency={currency}
        donorName={paymentDetails.fullName || paymentDetails.name || paymentData.name || user?.name || ""}
        dateLabel={(Number.isNaN(paidOn.getTime()) ? new Date() : paidOn).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
        transactionId={transactionId}
        paymentMethod={
          { upi: "UPI", card: "Card", netbanking: "Net banking", wallet: "Wallet", emi: "EMI" }[methodLabel] ||
          (methodLabel && methodLabel !== "online" ? paymentData.paymentMethod : "Online")
        }
        isMonthly={isMonthlyThankYou}
        on80G={handle80GForm}
      />
    );
  }

  return (
    <Box
      sx={{
        width: "94.44%",
        maxWidth: "1440px",
        margin: "0 auto",
        paddingTop: isMobile ? "20px" : "40px",
      }}
    >
      {/* Banner Section */}
      <div
        style={{
          position: "relative",
          width: "100%",
          maxWidth: "1440px",
          marginInline: "auto",
          minHeight: isMobile ? "300px" : "509px",
          background: "#FAEBF8",
          borderRadius: isMobile ? "16px" : "22px",
          overflow: "hidden",
          padding: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: isMobile ? "20px" : "40px",
        }}
      >
        <img
          src={Images.thankyouBanner}
          alt="center heart"
          style={{
            position: "absolute",
            left: "0%",
            top: "0%",
            width: "100%",
            height: "100%",
            objectFit: "cover",
            zIndex: 1,
          }}
        />
        <div
          style={{
            color: "#249568",
            fontWeight: 700,
            fontSize: isMobile ? "22px" : "44px",
            fontFamily: "inherit",
            zIndex: 2,
            position: "relative",
          }}
        >
          THANK YOU
        </div>
      </div>

      {/* Receipt Card Section */}
      <Box
        sx={{
          maxWidth: "760px",
          margin: "0 auto",
          background: "#FFFFFF",
          borderRadius: isMobile ? "16px" : "34px",
          boxShadow: "0 11.566px 34.697px 0 rgba(170, 170, 170, 0.25), 0 4px 12px 0 rgba(0, 0, 0, 0.15)",
          padding: isMobile ? "16px" : "34px",
          marginTop: isMobile ? "10px" : "0",
          marginBottom: isMobile ? "20px" : "40px",
          width: "100%",
        }}
      >
        {/* Header Section */}
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "row" : "row",
            justifyContent: "space-between",
            alignItems: isMobile ? "flex-start" : "flex-start",
            marginBottom: "32px",
            borderBottom: "1px solid #E6E6E6",
            paddingBottom: "24px",
            gap: isMobile ? "16px" : "0",
          }}
        >
          <Box sx={{ display: "flex", gap: "16px", alignItems: "center" }}>
            <Box>
              <img
                src={Images.thankyouSuccessIcon}
                alt="center heart"
                style={{
                  width: isMobile ? "30px" : "69px",
                  height: isMobile ? "30px" : "69px",
                }}
              />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontSize: isMobile ? "12px" : "15px",
                  fontWeight: 400,
                  color: "#4A4A49",
                  marginBottom: isMobile ? "4px" : "12px",
                }}
              >
                {isSuccess ? "Payment Success" : "Payment Failed"}
              </Typography>
              <Typography
                sx={{
                  fontSize: isMobile ? "16px" : "24px",
                  fontWeight: 700,
                  color: "#1D1D1B",
                }}
              >
                {formatAmount(amount, currency)}
              </Typography>
            </Box>
          </Box>
          {isSuccess && (
            <CampaignButtonNoArrowVariant
              text="Download 80G Form"
              isMobile={isMobile}
              onClick={handle80GForm}
            />
          )}
        </Box>

        {/* Transaction Details */}
        <Box
          sx={{
            display: isMobile ? "flex" : "grid",
            flexDirection: isMobile ? "column" : "unset",
            gridTemplateColumns: isMobile ? "none" : "1fr 1fr",
            gap: isMobile ? "12px" : "24px",
          }}
        >
          {/* Left Column */}
          {/* Transaction ID */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: isMobile ? "8px" : "20px",
              textAlign: "left",
            }}
          >
            <Box sx={{ display: isMobile ? "flex" : "block", justifyContent: "space-between", alignItems: "center" }}>
              <Typography
                sx={{
                  fontSize: isMobile ? "12px" : "16px",
                  fontWeight: 500,
                  color: "#686866",
                }}
              >
                Transaction ID
              </Typography>
              {isMobile && (
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "#1D1D1B",
                    textAlign: "right",
                  }}
                >
                  {transactionId}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: isMobile ? "flex" : "block", justifyContent: "space-between", alignItems: "center" }}>
              <Typography
                sx={{
                  fontSize: isMobile ? "12px" : "16px",
                  fontWeight: 500,
                  color: "#686866",
                }}
              >
                Payment Date & Time
              </Typography>
              {isMobile && (
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "#1D1D1B",
                    textAlign: "right",
                    flex: 1,
                    ml: 1,
                  }}
                >
                  {paymentDateTime}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: isMobile ? "flex" : "block", justifyContent: "space-between", alignItems: "center" }}>
              <Typography
                sx={{
                  fontSize: isMobile ? "12px" : "16px",
                  fontWeight: 500,
                  color: "#686866",
                }}
              >
                Payment Method
              </Typography>
              {isMobile && (
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "#1D1D1B",
                    textAlign: "right",
                    flex: 1,
                    ml: 1,
                  }}
                >
                  {paymentMethod}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: isMobile ? "flex" : "block", justifyContent: "space-between", alignItems: "center" }}>
              <Typography
                sx={{
                  fontSize: isMobile ? "12px" : "16px",
                  fontWeight: 500,
                  color: "#686866",
                }}
              >
                Payment Status
              </Typography>
              {isMobile && (
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: 600,
                    color: isSuccess ? "#23A26D" : "#F04951",
                    textAlign: "right",
                  }}
                >
                  {status}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: isMobile ? "flex" : "block", justifyContent: "space-between", alignItems: "center" }}>
              <Typography
                sx={{
                  fontSize: isMobile ? "12px" : "16px",
                  fontWeight: 500,
                  color: "#686866",
                }}
              >
                Total Amount
              </Typography>
              {isMobile && (
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "#1D1D1B",
                    textAlign: "right",
                  }}
                >
                  {formatAmount(amount, currency)}
                </Typography>
              )}
            </Box>
          </Box>
          {!isMobile && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "20px",
                textAlign: "right",
              }}
            >
              <Typography
                sx={{
                  fontSize: "16px",
                  fontWeight: 600,
                  color: "#1D1D1B",
                }}
              >
                {transactionId}
              </Typography>
              <Typography
                sx={{
                  fontSize: "16px",
                  fontWeight: 600,
                  color: "#1D1D1B",
                }}
              >
                {paymentDateTime}
              </Typography>
              <Typography
                sx={{
                  fontSize: "16px",
                  fontWeight: 600,
                  color: "#1D1D1B",
                }}
              >
                {paymentMethod}
              </Typography>
              <Typography
                sx={{
                  fontSize: "16px",
                  fontWeight: 600,
                  color: isSuccess ? "#23A26D" : "#F04951",
                }}
              >
                {status}
              </Typography>
              <Typography
                sx={{
                  fontSize: "16px",
                  fontWeight: 600,
                  color: "#1D1D1B",
                }}
              >
                {formatAmount(amount, currency)}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ThankYouBanner;
